/**
 * Authentication store
 * Manages Firebase Auth state with proper bootstrap pattern
 */
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { onAuthStateChanged, signOut } from 'firebase/auth'
import { getFirebaseAuth } from '../../core/firebase/firebase.js'
import { getDocument } from '../../core/firebase/firestore.js'
import { getRoleClaim, subscribeToTokenChanges } from '../../core/firebase/auth.js'
import { startUserActivityTracking } from '../../core/services/activity.js'
import logger, { setLoggerAdminStatus } from '../../core/services/logger.js'

// Lazy import to avoid circular dependency
let _cartStore = null
function getCartStore() {
  if (!_cartStore) {
    const { useCartStore } = require('./cart.js')
    _cartStore = useCartStore()
  }
  return _cartStore
}

// Roles that are allowed to have linked client catalog
const CLIENT_LINKED_ROLES = ['client', 'admin', 'agent']

const log = logger.createScoped('AuthStore')

/**
 * Normalize Firebase user to plain object
 * Avoids storing reactive Firebase objects
 * @param {Object} firebaseUser
 * @returns {Object|null}
 */
function normalizeUser(firebaseUser) {
  if (!firebaseUser) return null

  return {
    uid: firebaseUser.uid,
    email: firebaseUser.email,
    displayName: firebaseUser.displayName,
    phoneNumber: firebaseUser.phoneNumber,
    photoURL: firebaseUser.photoURL,
    emailVerified: firebaseUser.emailVerified,
    isAnonymous: firebaseUser.isAnonymous,
    metadata: {
      creationTime: firebaseUser.metadata?.creationTime,
      lastSignInTime: firebaseUser.metadata?.lastSignInTime,
    },
    providerData: firebaseUser.providerData?.map((p) => ({
      providerId: p.providerId,
      uid: p.uid,
      displayName: p.displayName,
      email: p.email,
      phoneNumber: p.phoneNumber,
      photoURL: p.photoURL,
    })),
  }
}

export const useAuthStore = defineStore('auth', () => {
  // ============================================
  // State
  // ============================================

  /**
   * User state container
   * - authData: null = unresolved (initial phase)
   * - authData: false = signed out
   * - authData: object = signed in
   * - profile: null = signed out / profile missing / fetch failed
   * - profile: object = profile doc from Users/<uid>
   * - client: null = not loaded / no client linked / role not allowed
   * - client: object = client doc from ClientsV2/<client_makat>
   */
  const user = ref({
    authData: null,
    profile: null,
    client: null,
  })

  /**
   * Loading state - true during initial auth resolution
   */
  const loading = ref(true)

  /**
   * Auth error state
   */
  const error = ref(null)

  /**
   * Temporary agent override for admin impersonation
   * Used when admins need to view catalogs as specific agents
   */
  const temporaryAgentOverride = ref(null)
  const impersonatedAgentName = ref(null)

  /**
   * Session-only active-client tracking
   * - mainClientMakat: the user's original client_makat at sign-in time
   *   (the one stored on Users/{uid}). Used to mark the "main" card in
   *   the sub-client selector.
   * - activeClientSelected: true once the user has explicitly chosen a
   *   client for this session via setActiveClient(). Resets on sign-in/out.
   */
  const mainClientMakat = ref(null)
  const activeClientSelected = ref(false)

  // Internal: track if bootstrap has been called
  let bootstrapPromise = null
  let bootstrapResolve = null
  let unsubscribeAuth = null
  let unsubscribeToken = null
  let stopActivity = null
  // Promise that resolves when the current auth-listener IIFE finishes.
  // Set at the start of every onAuthStateChanged callback, cleared in its finally.
  // Used by waitForAuthResolution so callers wait for profile/client loading
  // on every auth event (not just the initial bootstrap).
  let resolutionInFlight = null

  // ============================================
  // Getters
  // ============================================

  /**
   * Check if user is authenticated
   * Only true when authData is a valid object (not null, not false)
   */
  const isAuthenticated = computed(() => {
    return user.value.authData !== null && user.value.authData !== false
  })

  /**
   * Check if auth state is resolved
   * True when authData is not null (either signed in or signed out)
   */
  const isAuthResolved = computed(() => {
    return user.value.authData !== null
  })

  /**
   * Get user display name
   */
  const userDisplayName = computed(() => {
    const data = user.value.authData
    if (!data || data === false) return 'משתמש'
    return data.displayName || user.value.profile?.name || data.email || 'משתמש'
  })

  /**
   * Get user email
   */
  const userEmail = computed(() => {
    const data = user.value.authData
    if (!data || data === false) return null
    return data.email
  })

  /**
   * Get user UID
   */
  const userId = computed(() => {
    const data = user.value.authData
    if (!data || data === false) return null
    return data.uid
  })

  /**
   * Get user role
   * Returns role from custom claims ONLY (stored in profile.role)
   * No Firestore fallback - claims are authoritative
   */
  const userRole = computed(() => {
    return user.value.profile?.role ?? null
  })

  /**
   * Get user's agent ID (for agent users)
   * Returns agent number from profile
   */
  const userAgentId = computed(() => {
    return user.value.profile?.agent ?? null
  })

  const userPhotoURL = computed(() => {
    return user.value.profile?.photoURL || null
  })

  /**
   * Check if user is admin
   */
  const isAdmin = computed(() => {
    return userRole.value === 'admin'
  })

  /**
   * Check if user is agent
   */
  const isAgent = computed(() => {
    return userRole.value === 'agent'
  })

  /**
   * Get effective agent number (with override support)
   * Returns temporary override if set, otherwise user's actual agent number
   */
  const effectiveAgentNumber = computed(() => {
    // Override takes precedence (for admin impersonation)
    if (temporaryAgentOverride.value !== null) {
      return temporaryAgentOverride.value
    }
    // Fall back to user's actual agent number
    return userAgentId.value
  })

  /**
   * Check if currently impersonating an agent
   */
  const isImpersonatingAgent = computed(() => {
    return temporaryAgentOverride.value !== null
  })

  // ============================================
  // Actions
  // ============================================

  /**
   * Set user as signed in
   * @param {Object} userData - Normalized user data
   */
  function setUser(userData) {
    const normalized = normalizeUser(userData)
    user.value.authData = normalized || false
    log.info('User state updated', { userId: normalized?.uid, signedIn: !!normalized })
  }

  /**
   * Set user as signed out
   */
  function setSignedOut() {
    user.value.authData = false
    user.value.profile = null
    user.value.client = null
    mainClientMakat.value = null
    activeClientSelected.value = false
    setLoggerAdminStatus(false)
    log.info('User signed out')
  }

  /**
   * Set loading state
   * @param {boolean} state
   */
  function setLoading(state) {
    loading.value = state
  }

  /**
   * Set error state
   * @param {Error|string|null} err
   */
  function setError(err) {
    error.value = err
    if (err) {
      log.error('Auth error', err)
    }
  }

  /**
   * Clear error state
   */
  function clearError() {
    error.value = null
  }

  /**
   * Handle token refresh events
   * Updates role from claims without showing loading overlay
   * @param {Object|null} firebaseUser
   */
  async function handleTokenRefresh(firebaseUser) {
    // Only process if we have a signed-in user and bootstrap is complete
    if (!firebaseUser || loading.value) {
      return
    }

    try {
      const claimRole = await getRoleClaim(firebaseUser)
      const currentRole = user.value.profile?.role

      // Only update if role actually changed
      if (claimRole !== currentRole) {
        log.info('Role claim updated via token refresh', {
          uid: firebaseUser.uid,
          oldRole: currentRole,
          newRole: claimRole,
        })

        // Update profile.role without touching other profile data
        if (user.value.profile) {
          user.value.profile = { ...user.value.profile, role: claimRole }
        } else {
          user.value.profile = { id: firebaseUser.uid, role: claimRole }
        }
        // Notify logger of admin status change
        setLoggerAdminStatus(claimRole === 'admin')
      }
    } catch (err) {
      log.error('Failed to refresh role claim', err)
    }
  }

  /**
   * Initialize auth bootstrap
   * Attaches onAuthStateChanged listener ONCE
   * Returns a promise that resolves when initial auth state is known
   * @returns {Promise<void>}
   */
  function initAuthBootstrap() {
    // Return existing promise if already bootstrapping
    if (bootstrapPromise) {
      log.debug('Auth bootstrap already in progress')
      return bootstrapPromise
    }

    log.info('Starting auth bootstrap')
    loading.value = true

    // Create promise for external await
    bootstrapPromise = new Promise((resolve) => {
      bootstrapResolve = resolve
    })

    // Try to get auth - may fail if Firebase not initialized yet
    let auth
    try {
      auth = getFirebaseAuth()
    } catch {
      log.warn('Firebase not ready, will retry on mount')
      // Resolve with signed out state if Firebase fails
      user.value.authData = false
      user.value.profile = null
      setLoggerAdminStatus(false)
      loading.value = false
      if (bootstrapResolve) {
        bootstrapResolve()
      }
      return bootstrapPromise
    }

    // Attach auth state listener
    unsubscribeAuth = onAuthStateChanged(
      auth,
      (firebaseUser) => {
        const wasLoading = loading.value

        // Track this resolution so waitForAuthResolution() can await profile/client
        // loading on every auth event, not just the initial bootstrap.
        let resolveInFlight
        const thisResolution = new Promise((r) => {
          resolveInFlight = r
        })
        resolutionInFlight = thisResolution

        // Use async IIFE to handle profile + claims fetch
        ;(async () => {
          try {
            if (firebaseUser) {
              // User is signed in
              const normalized = normalizeUser(firebaseUser)
              user.value.authData = normalized
              log.info('Auth state: signed in', { uid: normalized.uid, email: normalized.email })

              // Step 1: Fetch user profile from Firestore
              let profile = null
              try {
                profile = await getDocument('Users', normalized.uid)
                if (profile) {
                  log.info('User profile loaded from Firestore', { uid: normalized.uid })
                } else {
                  log.warn('User profile not found in Firestore', { uid: normalized.uid })
                }
              } catch (profileErr) {
                log.error('Failed to fetch user profile', profileErr)
              }

              // Step 2: Fetch role from custom claims (authoritative source)
              const claimRole = await getRoleClaim(firebaseUser)
              log.info('Role claim fetched', { uid: normalized.uid, claimRole })

              // Step 3: Build profile with claims-only role
              // If no Firestore profile exists, create minimal container
              if (profile) {
                // Overwrite any Firestore role with claims-only role
                user.value.profile = { ...profile, role: claimRole }
              } else {
                // Create minimal profile container for role
                user.value.profile = { id: normalized.uid, role: claimRole }
              }

              // Capture the original client_makat as "main" (used by the
              // sub-client selector) and reset the session selection flag.
              mainClientMakat.value = profile?.client_makat ? String(profile.client_makat) : null
              activeClientSelected.value = false

              // Notify logger of admin status
              setLoggerAdminStatus(claimRole === 'admin')

              // Step 3.5: Auto-load client for 'client' role users
              // This ensures user.client is available immediately after login
              // (Agents select their client manually in the catalog)
              if (claimRole === 'client') {
                await loadUserClientIfAvailable()
              }

              // Step 4: Set up token refresh listener (only on first auth)
              if (wasLoading && !unsubscribeToken) {
                unsubscribeToken = subscribeToTokenChanges(handleTokenRefresh)
                log.debug('Token refresh listener attached')
              }

              // Step 5: Start activity tracking
              stopActivity?.()
              stopActivity = startUserActivityTracking({
                uid: normalized.uid,
                getProfile: () => user.value.profile,
              })
            } else {
              // User is signed out
              user.value.authData = false
              user.value.profile = null
              mainClientMakat.value = null
              activeClientSelected.value = false
              setLoggerAdminStatus(false)
              log.info('Auth state: signed out')

              // Clean up token listener on sign out
              if (unsubscribeToken) {
                unsubscribeToken()
                unsubscribeToken = null
              }

              // Stop activity tracking on sign out
              if (stopActivity) {
                stopActivity()
                stopActivity = null
              }

              // Reset cart on logout
              try {
                const cartStore = getCartStore()
                cartStore.resetOnLogout()
              } catch (err) {
                log.debug('Cart reset skipped (store not ready)', err)
              }
            }

            // Only set loading false and resolve on first callback
            if (wasLoading) {
              loading.value = false
              if (bootstrapResolve) {
                bootstrapResolve()
                bootstrapResolve = null
              }
            }
          } finally {
            if (resolutionInFlight === thisResolution) {
              resolutionInFlight = null
            }
            resolveInFlight()
          }
        })()
      },
      (err) => {
        log.error('Auth state listener error', err)
        error.value = err
        user.value.authData = false
        user.value.profile = null
        mainClientMakat.value = null
        activeClientSelected.value = false
        setLoggerAdminStatus(false)
        loading.value = false
        if (bootstrapResolve) {
          bootstrapResolve()
          bootstrapResolve = null
        }
      }
    )

    return bootstrapPromise
  }

  /**
   * Wait for auth to be resolved.
   *
   * Resolves only after the current auth-listener IIFE has finished — meaning
   * profile and (for client-role users) the linked client doc have been loaded.
   * Previously this short-circuited as soon as authData was set, which let
   * route guards proceed mid-IIFE while user.profile was still null and the
   * catalog would fall back to the default '10001' assortment.
   *
   * @returns {Promise<void>}
   */
  async function waitForAuthResolution() {
    // A listener IIFE is currently running (initial bootstrap OR a subsequent
    // login/logout). Wait for it to finish so profile/client are populated.
    if (resolutionInFlight) {
      await resolutionInFlight
      return
    }
    // Bootstrap hasn't started yet — kick it off and await it
    if (user.value.authData === null) {
      if (bootstrapPromise) {
        return bootstrapPromise
      }
      return initAuthBootstrap()
    }
    // Auth fully resolved and nothing in flight
  }

  /**
   * Logout user
   * Signs out from Firebase and updates store
   */
  async function logout() {
    try {
      // Clear agent impersonation before logout
      clearTemporaryAgentOverride()

      const auth = getFirebaseAuth()
      await signOut(auth)
      // State will be updated by onAuthStateChanged listener
      log.info('Logout successful')
    } catch (err) {
      log.error('Logout failed', err)
      // Force local state update even if Firebase fails
      user.value.authData = false
      throw err
    }
  }

  /**
   * Load user's linked client document if needed
   * Rules:
   * - If role not in ['client','admin','agent'] -> set client = null
   * - If profile.client_makat is empty -> set client = null
   * - If already loaded and client.makat matches -> skip (avoid extra reads)
   * - Otherwise fetch ClientsV2/{client_makat} and store as user.client
   * @returns {Promise<{client: Object|null, clientMissing: boolean}>}
   */
  async function loadUserClientIfAvailable() {
    const profile = user.value.profile
    const role = profile?.role

    // Check if role is allowed for client linking
    if (!role || !CLIENT_LINKED_ROLES.includes(role)) {
      log.debug('Role not allowed for client linking', { role })
      user.value.client = null
      return { client: null, clientMissing: false }
    }

    const clientMakat = profile?.client_makat
    if (!clientMakat) {
      log.debug('No client_makat in profile')
      user.value.client = null
      return { client: null, clientMissing: false }
    }

    // Check if already loaded (avoid extra reads)
    if (user.value.client && user.value.client.makat === clientMakat) {
      log.debug('Client already loaded', { clientMakat })
      return { client: user.value.client, clientMissing: false }
    }

    // Fetch client document from ClientsV2
    log.info('Loading linked client', { clientMakat })
    try {
      const clientDoc = await getDocument('ClientsV2', clientMakat)

      if (!clientDoc) {
        log.warn('Client document not found', { clientMakat })
        user.value.client = null
        return { client: null, clientMissing: true }
      }

      // Store client with makat for cache check
      user.value.client = { ...clientDoc, makat: clientMakat }
      log.info('Client loaded successfully', {
        clientMakat,
        merakez: clientDoc.merakez,
        priceListId: clientDoc.price_list_id,
      })

      return { client: user.value.client, clientMissing: false }
    } catch (err) {
      log.error('Failed to load client document', err)
      user.value.client = null
      return { client: null, clientMissing: true }
    }
  }

  /**
   * Switch the session's active client (sub-client selection).
   * Mutates profile.client_makat in memory ONLY — the Firestore Users doc
   * is never written, so this resets on reload / sign-out.
   * Re-loads user.client so downstream readers see the new ClientsV2 doc.
   */
  async function setActiveClient(newMakat, newName) {
    if (!user.value.profile) return { client: null, clientMissing: false }
    const makat = String(newMakat || '')
    if (!makat) return { client: null, clientMissing: false }

    user.value.profile = {
      ...user.value.profile,
      client_makat: makat,
      client_name: String(newName || ''),
    }
    user.value.client = null
    const result = await loadUserClientIfAvailable()
    activeClientSelected.value = true
    log.info('Active client switched', { makat })
    return result
  }

  /**
   * Reset store to initial state
   */
  function reset() {
    user.value.authData = null
    user.value.profile = null
    user.value.client = null
    mainClientMakat.value = null
    activeClientSelected.value = false
    setLoggerAdminStatus(false)
    loading.value = true
    error.value = null
    bootstrapPromise = null
    bootstrapResolve = null
    resolutionInFlight = null
    if (unsubscribeAuth) {
      unsubscribeAuth()
      unsubscribeAuth = null
    }
    if (unsubscribeToken) {
      unsubscribeToken()
      unsubscribeToken = null
    }
    if (stopActivity) {
      stopActivity()
      stopActivity = null
    }
  }

  /**
   * Cleanup - call on app unmount if needed
   */
  function cleanup() {
    if (unsubscribeAuth) {
      unsubscribeAuth()
      unsubscribeAuth = null
    }
    if (unsubscribeToken) {
      unsubscribeToken()
      unsubscribeToken = null
    }
    if (stopActivity) {
      stopActivity()
      stopActivity = null
    }
  }

  /**
   * Set temporary agent override for admin impersonation
   * Allows admins to view catalogs as specific agents
   * @param {number} agentNumber - Agent number to impersonate
   * @param {string} agentName - Optional agent name for display
   * @returns {boolean} - True if override was set, false if not allowed
   */
  function setTemporaryAgentOverride(agentNumber, agentName = null) {
    if (!isAdmin.value) {
      log.warn('Only admins can use agent impersonation')
      return false
    }
    temporaryAgentOverride.value = agentNumber
    impersonatedAgentName.value = agentName
    log.info('Agent impersonation activated', { agentNumber, agentName })
    return true
  }

  /**
   * Clear temporary agent override
   * Restores normal agent number behavior
   */
  function clearTemporaryAgentOverride() {
    if (temporaryAgentOverride.value !== null) {
      log.info('Agent impersonation cleared', { agentNumber: temporaryAgentOverride.value })
      temporaryAgentOverride.value = null
      impersonatedAgentName.value = null
    }
  }

  return {
    // State
    user,
    loading,
    error,
    // Getters
    isAuthenticated,
    isAuthResolved,
    userDisplayName,
    userEmail,
    userId,
    userRole,
    userAgentId,
    userPhotoURL,
    isAdmin,
    isAgent,
    effectiveAgentNumber,
    isImpersonatingAgent,
    impersonatedAgentName,
    mainClientMakat,
    activeClientSelected,
    // Actions
    setUser,
    setSignedOut,
    setLoading,
    setError,
    clearError,
    initAuthBootstrap,
    waitForAuthResolution,
    loadUserClientIfAvailable,
    setActiveClient,
    logout,
    reset,
    cleanup,
    setTemporaryAgentOverride,
    clearTemporaryAgentOverride,
  }
})
